import React, { useEffect, useMemo, useState } from "react";
import { apiGet, apiPost, formatDateTime } from "@/lib/api";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import KpiTile from "@/components/dashboard/KpiTile";
import LoadingState from "@/components/common/LoadingState";
import ErrorState from "@/components/common/ErrorState";
import {
  Mail, RefreshCcw, Send, AlertCircle, CheckCircle2,
  ScrollText, Inbox, Hourglass,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_BADGE = {
  sent:        "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30",
  dev_console: "bg-[hsl(var(--info))]/15 text-[hsl(var(--info))] border-[hsl(var(--info))]/30",
  failed:      "bg-[hsl(var(--destructive))]/15 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/30",
  skipped:     "bg-muted text-muted-foreground border-border",
};

const PAGE_SIZE = 25;

const EmailLogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [types, setTypes] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({ sent: 0, failed: 0, total: 0, skipped: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [retrying, setRetrying] = useState(null);

  const params = useMemo(() => ({
    limit: PAGE_SIZE,
    skip: page * PAGE_SIZE,
    status: statusFilter,
    email_type: typeFilter,
  }), [page, statusFilter, typeFilter]);

  const load = async (opts = {}) => {
    if (!opts.silent) setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams(params).toString();
      const [logsRes, statsRes] = await Promise.all([
        apiGet(`/admin/email-logs?${qs}`),
        apiGet(`/admin/email-stats`),
      ]);
      setLogs(logsRes.items || []);
      setTypes(logsRes.types || []);
      setTotal(logsRes.total || 0);
      setStats(statsRes || {});
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load email logs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [page, statusFilter, typeFilter]);

  const onRetry = async (log) => {
    setRetrying(log.log_id);
    try {
      const r = await apiPost(`/admin/email-logs/${log.log_id}/retry`);
      if (r.status === "sent" || r.status === "dev_console") {
        toast.success("Retry succeeded");
      } else if (r.status === "failed") {
        toast.error("Retry failed — check error message");
      } else {
        toast.info(`Retry: ${r.status}`);
      }
      load({ silent: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retry failed");
    } finally {
      setRetrying(null);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  if (loading && logs.length === 0) return <LoadingState message="Loading email logs…" />;
  if (error && logs.length === 0) return <ErrorState message={error} onRetry={() => load()} />;

  return (
    <div className="space-y-6" data-testid="email-logs-page">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiTile
          label="Sent today"
          value={stats.sent ?? 0}
          icon={CheckCircle2}
          tone="success"
          testId="kpi-sent-today"
        />
        <KpiTile
          label="Failed today"
          value={stats.failed ?? 0}
          icon={AlertCircle}
          tone={stats.failed > 0 ? "destructive" : "muted"}
          testId="kpi-failed-today"
        />
        <KpiTile
          label="Skipped today"
          value={stats.skipped ?? 0}
          icon={Hourglass}
          tone="muted"
          testId="kpi-skipped-today"
        />
        <KpiTile
          label="Total today"
          value={stats.total ?? 0}
          icon={Inbox}
          tone="primary"
          testId="kpi-total-today"
        />
      </div>

      {/* Filter bar + table */}
      <Card className="border bg-card">
        <CardHeader className="flex flex-row items-start gap-4 justify-between">
          <div>
            <CardTitle className="font-display text-base flex items-center gap-2">
              <ScrollText className="h-4 w-4 text-primary" /> Email logs
            </CardTitle>
            <CardDescription>
              Every outbound email is logged here. {total.toLocaleString()} total in the system.
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" className="gap-2" onClick={() => load()} data-testid="refresh-logs">
            <RefreshCcw className="h-3.5 w-3.5" /> Refresh
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <label className="text-xs uppercase tracking-wider text-muted-foreground font-medium block mb-1.5">Status</label>
              <Select value={statusFilter} onValueChange={(v) => { setPage(0); setStatusFilter(v); }}>
                <SelectTrigger data-testid="filter-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="dev_console">Dev console</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                  <SelectItem value="skipped">Skipped</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <label className="text-xs uppercase tracking-wider text-muted-foreground font-medium block mb-1.5">Type</label>
              <Select value={typeFilter} onValueChange={(v) => { setPage(0); setTypeFilter(v); }}>
                <SelectTrigger data-testid="filter-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  {types.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full text-xs" data-testid="email-logs-table">
              <thead className="bg-muted/40 text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Sent at</th>
                  <th className="px-3 py-2 text-left">To</th>
                  <th className="px-3 py-2 text-left">Type</th>
                  <th className="px-3 py-2 text-left">Subject</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Error</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 && (
                  <tr><td colSpan={7} className="px-3 py-8 text-center text-muted-foreground">No emails match the current filters.</td></tr>
                )}
                {logs.map((l) => (
                  <tr key={l.log_id} className="border-t border-border" data-testid={`log-row-${l.log_id}`}>
                    <td className="px-3 py-2 text-muted-foreground tabular whitespace-nowrap">{formatDateTime(l.sent_at)}</td>
                    <td className="px-3 py-2">{l.to}</td>
                    <td className="px-3 py-2 text-muted-foreground">{l.email_type}</td>
                    <td className="px-3 py-2 max-w-[260px] truncate" title={l.subject}>{l.subject}</td>
                    <td className="px-3 py-2">
                      <Badge variant="outline" className={`${STATUS_BADGE[l.status] || ""} text-[10px] gap-1`}>
                        {l.status === "sent" && <CheckCircle2 className="h-3 w-3" />}
                        {l.status === "failed" && <AlertCircle className="h-3 w-3" />}
                        {l.status}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 max-w-[220px] truncate text-[hsl(var(--destructive))]" title={l.error_message || ""}>
                      {l.error_message || <span className="text-muted-foreground/50">—</span>}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {(l.status === "failed" || l.status === "skipped") ? (
                        <TooltipProvider delayDuration={150}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 gap-1.5"
                                disabled={!!retrying}
                                onClick={() => onRetry(l)}
                                data-testid={`retry-${l.log_id}`}
                              >
                                {retrying === l.log_id ? (
                                  <RefreshCcw className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                  <Send className="h-3.5 w-3.5" />
                                )}
                                Retry
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Resend this email using the original body</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ) : (
                        <span className="text-muted-foreground/50">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>
              Showing {logs.length === 0 ? 0 : page * PAGE_SIZE + 1}–{page * PAGE_SIZE + logs.length} of {total}
            </span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} data-testid="page-prev">
                Previous
              </Button>
              <Button size="sm" variant="outline" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="page-next">
                Next
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lightweight legend */}
      <Card className="border bg-card">
        <CardContent className="py-4 flex flex-wrap items-center gap-4 text-[11px] text-muted-foreground">
          <span className="font-medium">Statuses:</span>
          <span className="inline-flex items-center gap-1.5"><Badge variant="outline" className={STATUS_BADGE.sent}>sent</Badge> delivered to SMTP</span>
          <span className="inline-flex items-center gap-1.5"><Badge variant="outline" className={STATUS_BADGE.dev_console}>dev_console</Badge> dev mode — logged only</span>
          <span className="inline-flex items-center gap-1.5"><Badge variant="outline" className={STATUS_BADGE.failed}>failed</Badge> 3 retries exhausted</span>
          <span className="inline-flex items-center gap-1.5"><Badge variant="outline" className={STATUS_BADGE.skipped}>skipped</Badge> SMTP misconfigured</span>
          <span className="inline-flex items-center gap-1.5 ml-auto"><Mail className="h-3.5 w-3.5" /> Bodies retained for retries.</span>
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailLogsPage;
